import React from 'react';
import classNames from 'classnames';
import _ from 'lodash';

import classes from '@nerdwallet/base-styles/classes';
import { spacing } from '@nerdwallet/base-styles/variables';
import StructuredContent from '@nerdwallet/structured-content';
import {
  Accent,
  CenteredContainer,
  Stack,
  Box,
  Columns,
  Column,
} from '@nerdwallet/currency';
import {
  Container as ABContainer,
  Variant,
  Control,
} from '@nerdwallet/nw-client-lib/ab';

import ABConfig from '~/lib/ab';
import ContentPageHeader from '~/client/components/content-page-header/content-page-header';
import EditorialStandards from '~/client/components/editorial-standards';
import ArticleStructuredContent from '~/client/components/article-structured-content/article-structured-content';
import {
  augmentWithToc,
  constructTableOfContentsDocument,
  TocSchema,
} from '~/lib/structured-content';

import AuthorBox from '~/client/components/author-box/author-box';
import Methodology from '~/client/components/methodology/methodology';
import ConnectedOutdatedPageNotification from '~/client/components/outdated-page-notification/outdated-page-notification';
import HouseAd from '~/client/components/house-ad/house-ad';
import NextSteps from '~/client/components/next-steps/next-steps';
import RelatedLinks from '~/client/components/related-links/related-links';

import styles from './general-article.module.less';
import FadeContainer from '../fade-container';
import { ArticleTemplateProps } from '~/lib/types';

const GeneralArticle: React.FC<ArticleTemplateProps> = ({
  hasTableOfContents,
  tableOfContentsHeading,
  graphqlArticle,
  activeExperiments,
}) => {
  const { items: tocItems, augmentedDoc } = augmentWithToc(
    graphqlArticle.content
  );

  // Renders AB component for any active experiments
  const renderABContainers = () => {
    return activeExperiments.map((activeExperiment) => {
      const testConfig = _.find(ABConfig, {
        id: activeExperiment.experiment,
      });
      if (!testConfig) return null;
      return (
        <ABContainer experimentID={testConfig.id}>
          <Control />
          <Variant variantName={testConfig.variant} />
        </ABContainer>
      );
    });
  };

  return (
    <CenteredContainer>
      {renderABContainers()}
      <Stack spacing={4}>
        {!graphqlArticle.heroBannerDocument && (
          <ContentPageHeader
            article={graphqlArticle}
            category={null}
            width={{ desktop: 8, mobile: 12 }}
            imageHasFullWidth
            hideEditorialStandards
          />
        )}
        <Columns>
          <Column width={{ desktop: 8, mobile: 12 }}>
            <Stack spacing={4}>
              {graphqlArticle.showEditorialDisclaimer && (
                <EditorialStandards article={graphqlArticle} />
              )}
              <ConnectedOutdatedPageNotification />
              {hasTableOfContents && tocItems.length && (
                <div className={classNames(styles['toc-wide-variant'])}>
                  <Accent size="small" className={classes('margin-bottom-3')}>
                    {tableOfContentsHeading}
                  </Accent>
                  <div className={styles.hideNotDesktop}>
                    <StructuredContent>
                      {constructTableOfContentsDocument({
                        items: tocItems,
                        hasJumpCTA: true,
                        showMore: true,
                      })}
                    </StructuredContent>
                  </div>
                  <div className={styles.hideDesktop}>
                    <StructuredContent>
                      {constructTableOfContentsDocument({
                        items: tocItems,
                        hasJumpCTA: true,
                      })}
                    </StructuredContent>
                  </div>
                </div>
              )}
              <main>
                <div className={classes('margin-bottom-4')}>
                  {hasTableOfContents ? (
                    <ArticleStructuredContent schema={TocSchema}>
                      {augmentedDoc}
                    </ArticleStructuredContent>
                  ) : (
                    <ArticleStructuredContent>
                      {graphqlArticle.content}
                    </ArticleStructuredContent>
                  )}
                </div>
                <AuthorBox article={graphqlArticle} />
                <Methodology article={graphqlArticle} />
              </main>
            </Stack>
          </Column>
          {RelatedLinks.shouldShow(graphqlArticle) && (
            <Column width={{ desktop: 4 }}>
              <Box
                display={{ desktop: 'block', mobile: 'none' }}
                position="relative"
                height="100"
                paddingLeft="3-half"
                style={{ paddingTop: '14.5em' }}
              >
                <FadeContainer offset={spacing['5']} fadeDistance={250}>
                  <RelatedLinks article={graphqlArticle} maxItems={4} />
                </FadeContainer>
              </Box>
            </Column>
          )}
        </Columns>

        {NextSteps.shouldShow(graphqlArticle) && (
          <NextSteps article={graphqlArticle} />
        )}
        <section className={styles.bottom}>
          {HouseAd.shouldShow(graphqlArticle) && (
            <Box marginBottom={4}>
              <HouseAd id={graphqlArticle.houseAd?.id} />
            </Box>
          )}
        </section>
      </Stack>
    </CenteredContainer>
  );
};

GeneralArticle.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  activeExperiments: [],
};

export default GeneralArticle;
